import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './config/authConfig';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import Login from './components/Login';
import AppRoutes from './components/AppRoutes';
import Navigation from './components/Navigation';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary';
import type { AuthValidator } from './utils/authValidator';

// Type augmentation for debugging window properties
declare global {  interface Window {
    msalInstance?: PublicClientApplication;
    debugMsal?: () => void;
    AuthValidator?: typeof AuthValidator;
    runAuthTest?: () => Promise<void>;    testMultiTenantAuth?: () => Promise<void>;
    checkAuthStatus?: () => { authenticated: boolean; account?: object; isFromAppTenant?: boolean };
  }
}

// Import auth validator for development debugging
if (process.env.NODE_ENV === 'development') {
  import('./utils/authValidator').then((authValidator) => {
    // Make auth validator available globally for testing
    window.AuthValidator = authValidator.AuthValidator;    window.runAuthTest = () => authValidator.AuthValidator.runFullAuthTest();    window.testMultiTenantAuth = () => authValidator.AuthValidator.testMultiTenantAuth();
    window.checkAuthStatus = () => authValidator.AuthValidator.checkAuthStatus();
    
    console.log('🔧 Authentication testing functions loaded:');
    console.log('  - window.runAuthTest() - Run full authentication test');
    console.log('  - window.testMultiTenantAuth() - Test multi-tenant authentication');
    console.log('  - window.checkAuthStatus() - Quick authentication status check');
    console.log('  - window.AuthValidator - Access to auth validator class');
  });
}

const msalInstance = new PublicClientApplication(msalConfig);

const App: React.FC = () => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        console.log('Initializing MSAL...');
        await msalInstance.initialize();
        console.log('MSAL initialized successfully');
        
        console.log('Handling redirect promise...');
        const response = await msalInstance.handleRedirectPromise();
        
        if (response) {
          console.log('🎉 Login successful:', response);
          console.log('User account:', response.account);
          console.log('Access token received:', !!response.accessToken);
        } else {
          console.log('No redirect response - checking for existing accounts...');
        }
        
        // Check for existing accounts
        const accounts = msalInstance.getAllAccounts();
        console.log('All accounts found:', accounts);
        
        if (accounts.length > 0) {
          console.log('Setting active account:', accounts[0]);
          msalInstance.setActiveAccount(accounts[0]);
        }
        
        setIsInitialized(true);
      } catch (error) {
        console.error('MSAL initialization or redirect handling error:', error);
        setIsInitialized(true); // Still allow app to render
      }
    };

    initializeMsal();
  }, []);

  // Add global access for debugging
  useEffect(() => {
    window.msalInstance = msalInstance;
    window.debugMsal = () => {
      console.log('🔍 MSAL Debug Info:');
      console.log('All accounts:', msalInstance.getAllAccounts());
      console.log('Active account:', msalInstance.getActiveAccount());
      console.log('Configuration:', msalConfig);
    };
  }, []);

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-pottery-cream">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-clay-600 mx-auto"></div>
          <p className="mt-4 text-clay-700">Authentifizierung wird initialisiert...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <Router>
          <div className="min-h-screen bg-pottery-sand">            <AuthenticatedTemplate>
              {/* <AuthorizationGuard> */}
                <Navigation />
                <main className="container mx-auto px-4 py-8">
                  <AppRoutes />
                </main>
              {/* </AuthorizationGuard> */}
            </AuthenticatedTemplate>
            
            <UnauthenticatedTemplate>
              <Login />
            </UnauthenticatedTemplate>
            
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
              toastClassName="!bg-pottery-cream !text-pottery-earth border border-clay-200"
            />
          </div>
        </Router>
      </MsalProvider>
    </ErrorBoundary>
  );
};

export default App; 